import getLogger from '../util/logger';
import {
  getAlbumCheckoutItems,
  isCartItemSelected,
} from '../util/ppd-purchase';
import { getErrorString } from '../util/normalize-errors';
import { sentryCaptureMessage } from '../util/sentry';
import { PPD_CART_ITEM_TYPES } from '../constants/ppd-purchase';

const log = getLogger('PurchaseAlbumPhotosAction');

export const SELECT_DOWNLOAD = 'ppd-purchase/SELECT_DOWNLOAD';
export const DESELECT_DOWNLOAD = 'ppd-purchase/DESELECT_DOWNLOAD';
export const SELECT_INDIVIDUAL_PRINT = 'ppd-purchase/SELECT_INDIVIDUAL_PRINT';
export const DESELECT_INDIVIDUAL_PRINT =
  'ppd-purchase/DESELECT_INDIVIDUAL_PRINT';
export const SELECT_PRINT_PACKAGE = 'ppd-purchase/SELECT_PRINT_PACKAGE';
export const DESELECT_PRINT_PACKAGE = 'ppd-purchase/DESELECT_PRINT_PACKAGE';
export const CHECKOUT_REQUEST = 'ppd-purchase/CHECKOUT_REQUEST';
export const CHECKOUT_SUCCESS = 'ppd-purchase/CHECKOUT_SUCCESS';
export const CHECKOUT_FAILURE = 'ppd-purchase/CHECKOUT_FAILURE';
export const SELECT_IDENTITY = 'ppd-purchase/SELECT_IDENTITY';
export const SELECT_SUBSCRIPTION = 'ppd-purchase/SELECT_SUBSCRIPTION';
export const DESELECT_SUBSCRIPTION = 'ppd-purchase/DESELECT_SUBSCRIPTION';
export const SELECT_ALBUM_CUSTOM_PRINT =
  'ppd-purchase/SELECT_ALBUM_CUSTOM_PRINT';
export const DESELECT_ALBUM_CUSTOM_PRINT =
  'ppd-purchase/DESELECT_ALBUM_CUSTOM_PRINT';
export const DESELECT_CART_ITEM = 'ppd-purchase/DESELECT_CART_ITEM';

export const selectDownload = photo => {
  return { type: SELECT_DOWNLOAD, photo };
};

export const deselectDownload = photo => {
  return { type: DESELECT_DOWNLOAD, photo };
};

export const selectPrint = (photo, printSize, quantity = 1) => {
  return {
    type: SELECT_INDIVIDUAL_PRINT,
    photo,
    printSize,
    quantity,
  };
};

export const deselectPrint = (photo, printSize) => {
  return { type: DESELECT_INDIVIDUAL_PRINT, photo, printSize };
};

export const selectPrintPackage = (
  photo,
  printPackage,
  quantity = 1,
  digitalDownloadUpsell = false,
) => {
  return {
    type: SELECT_PRINT_PACKAGE,
    photo,
    printPackage,
    quantity,
    digitalDownloadUpsell,
  };
};

export const deselectPrintPackage = (photo, printPackage) => {
  return { type: DESELECT_PRINT_PACKAGE, photo, printPackage };
};

export const selectSubscription = selectedIdentity => {
  return { type: SELECT_SUBSCRIPTION, selectedIdentity };
};

export const deselectSubscription = selectedIdentity => {
  return { type: DESELECT_SUBSCRIPTION, selectedIdentity };
};

export const selectAlbumCustomPrint = (
  selectedIdentity,
  albumCustomPrint,
  includeShipping = true,
) => {
  return {
    type: SELECT_ALBUM_CUSTOM_PRINT,
    selectedIdentity,
    albumCustomPrint,
    includeShipping,
  };
};

export const deselectAlbumCustomPrint = (
  selectedIdentity,
  albumCustomPrint,
) => {
  return {
    type: DESELECT_ALBUM_CUSTOM_PRINT,
    selectedIdentity,
    albumCustomPrint,
  };
};

export const deselectCartItem = id => {
  return {
    type: DESELECT_CART_ITEM,
    id,
  };
};

export const selectIdentity = selectedIdentity => {
  return { type: SELECT_IDENTITY, selectedIdentity };
};

const checkoutRequest = () => {
  return { type: CHECKOUT_REQUEST };
};

const checkoutSuccess = purchasedUrl => {
  return { type: CHECKOUT_SUCCESS, purchasedUrl };
};

const checkoutFailure = error => {
  return { type: CHECKOUT_FAILURE, error };
};

export const toggleSubscription = selectedIdentity => (dispatch, getState) => {
  const {
    ppdPurchase: { cart },
  } = getState();

  const inCart = isCartItemSelected(
    cart,
    selectedIdentity.id,
    PPD_CART_ITEM_TYPES.SUBSCRIPTION,
  );

  if (inCart) {
    dispatch(deselectSubscription(selectedIdentity));
  } else {
    dispatch(selectSubscription(selectedIdentity));
  }
};

export const toggleAlbumCustomPrint =
  (selectedIdentity, albumCustomPrint, includeShipping = true) =>
  (dispatch, getState) => {
    const {
      ppdPurchase: { cart },
    } = getState();

    const inCart = isCartItemSelected(
      cart,
      `${selectedIdentity.id}-${albumCustomPrint.id}`,
      PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT,
    );

    if (inCart) {
      dispatch(deselectAlbumCustomPrint(selectedIdentity, albumCustomPrint));
    } else {
      dispatch(
        selectAlbumCustomPrint(
          selectedIdentity,
          albumCustomPrint,
          includeShipping,
        ),
      );
    }
  };

export const checkout =
  ({
    userId,
    album,
    addressId,
    email,
    stripeTokenId,
    checkoutMutation,
    stripePaymentMethodId,
    stripeConnectAccountId,
  }) =>
  async (dispatch, getState) => {
    const {
      ppdPurchase: { cart },
    } = getState();
    log.info('checkout() :: Purchasing photos...', cart);

    dispatch(checkoutRequest());

    try {
      const { ppaItems, customPrintItems, photoItems } = getAlbumCheckoutItems(
        cart,
        album,
      );

      const res = await checkoutMutation({
        userId,
        photoItems,
        ppaItems,
        customPrintItems,
        addressId,
        email,
        stripeTokenId,
        stripePaymentMethodId,
        stripeConnectAccountId,
      });

      const checkoutData = res.data.checkout;

      if (checkoutData.is_paid) {
        dispatch(checkoutSuccess(checkoutData.purchased_url));

        log.info('checkout() :: Checkout success');
      } else {
        const errorMessage = checkoutData.errors.join('. ');

        sentryCaptureMessage(`[PPD Checkout Error] - ${errorMessage}`, {
          level: 'error',
          extra: {
            albumId: album.id,
            userId,
            addressId,
            email,
            errors: checkoutData.errors,
          },
        });

        dispatch(checkoutFailure(getErrorString(errorMessage)));

        log.error('checkout() :: Error:', errorMessage);
      }
    } catch (error) {
      log.error('checkout() :: Error:', error);

      dispatch(checkoutFailure(getErrorString(error)));
    }
  };
